import { useEffect, useState } from 'react';
import type { RefObject } from 'react';

/**
 * True once `ref`'s element has scrolled fully above the viewport top —
 * i.e. the top of the page has passed it — and back to false the moment
 * any part of it re-enters. IntersectionObserver-driven (no scroll
 * listeners): entries fire on boundary crossings and on viewport resizes,
 * so the answer stays correct at every resolution for free.
 */
export function useScrolledPast<T extends Element>(ref: RefObject<T | null>): boolean {
  const [past, setPast] = useState(false);
  const [el, setEl] = useState<T | null>(null);

  // Re-sample the ref every render: the observed element can mount late
  // (the hero state waits on the event fetch) or be swapped on a phase
  // change. Deliberately dependency-free — setEl with an unchanged node is
  // a no-op, so this can't loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setEl(ref.current);
  });

  useEffect(() => {
    if (!el || typeof IntersectionObserver === 'undefined') {
      setPast(false);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      // Above the top edge = out of view with its rect above y=0 (out of
      // view BELOW the viewport keeps a positive bottom).
      setPast(!entry.isIntersecting && entry.boundingClientRect.bottom <= 0);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);

  return past;
}
